/**
 * Very simple JSON Schema renderer.
 */

import { RJSFSchema, UiSchema } from '@rjsf/utils';
import { cn } from 'domain/core';
import { inOptions, SelectOption } from 'domain/utils/select.utils';
import { useMemo } from 'react';
import { Button, Input, Select } from 'components';
import { useTranslation } from 'react-i18next';
import { findWidget } from './widgets/Widgets';
import { useRelEntities, useIsEntity } from 'domain/hooks/hal/hal.service';
import { useOptions } from 'domain/utils/forms.utils';
import { OnChangeValue } from 'react-select';
import { RelationSelect } from './widgets/RelationSelect';

export const elementsClassName = 'w-full';

export const JsonRenderer = ({
	schema,
	schemaId,
	uiSchema,
	onChange,
	values,
	isNested,
}: {
	schema: RJSFSchema;
	schemaId: string;
	uiSchema: UiSchema;
	values: any;
	onChange: (values: any) => void;
	isNested?: boolean;
}) => {
	const { t } = useTranslation(['labels']);

	const isEntity = useIsEntity();

	const type = schema.type || 'object';

	const nestedClassNames = isNested ? 'flex flex-row flex-wrap pl-2' : '';

	if (type === 'object') {
		// Check if the id is an entity
		const { entity, rel } = isEntity(null, schema);
		if (type === 'object' && entity) {
			return (
				<RelationSelect
					rel={rel}
					uiSchema={uiSchema}
					values={[values]}
					onChange={(v) => onChange(v[0])}
				/>
			);
		}

		let properties = { ...schema.properties };
		const patternProperties = schema.patternProperties || {};
		properties = { ...patternProperties, ...properties };

		const result: React.ReactNode[] = [];

		for (const [key, val] of Object.entries(properties)) {
			if (
				Object.keys(uiSchema).filter((s) => !s.startsWith('ui:')).length > 0 &&
				!uiSchema[key]
			) {
				continue;
			}
			const keyUiSchema = uiSchema[key] ?? uiSchema?.['ui:nested']?.[key] ?? {};
			const currentSchema = val as RJSFSchema;
			const label = currentSchema.title || currentSchema.description || key;
			const currentValues =
				values[key] === undefined || values[key] === null ? {} : values[key];

			let size = isNested ? 'w-1/2 min-w-[100px] pr-2' : 'w-full';
			if (isNested) {
				const nested = uiSchema['ui:nested'] as { [key: string]: UiSchema };
				const uiSize = nested?.[label]?.['ui:size']?.trim();
				if (uiSize === '1/4') {
					size = `w-1/4 min-w-[20px] pr-2`;
				}
			}
			result.push(
				<div className={cn('mb-2 w-full', size)} key={key}>
					<div className={cn('pt-2 text-sm font-light text-neutral-900')}>
						{t(`labels:${label}`)}
					</div>
					<JsonRenderer
						schema={currentSchema}
						uiSchema={keyUiSchema}
						values={currentValues}
						schemaId={schemaId}
						onChange={(v) => {
							onChange({ ...values, [key]: v });
						}}
					/>
				</div>
			);
		}
		return <div className={nestedClassNames}>{result}</div>;
	}

	return (
		<PrimitiveSchema
			schema={schema}
			schemaId={schemaId}
			uiSchema={uiSchema}
			values={values}
			onChange={onChange}
		/>
	);
};

const PrimitiveSchema = ({
	schema,
	schemaId,
	uiSchema,
	values,
	onChange,
}: {
	schema: RJSFSchema;
	schemaId: string;
	uiSchema: UiSchema;
	values: any;
	onChange: (values: any) => void;
}) => {
	const { t } = useTranslation(['labels']);
	const type = schema.type;

	let element: React.ReactNode = (
		<>{/* For debugging JSON.stringify({ schema, uiSchema, }) */}</>
	);

	const isEntity = useIsEntity();

	const validValues = useMemo(() => {
		// Do not print out values if are undefined / object / array
		// If is a custom widget, allow
		if (
			typeof uiSchema === 'object' &&
			typeof uiSchema['ui:widget'] === 'string'
		) {
			return values;
		}

		if (
			values === undefined ||
			typeof values === 'object' ||
			Array.isArray(values)
		) {
			return undefined;
		}

		// If is writeOnly, then do not print out the value
		return values;
	}, [values]);

	const { readonly } = useSchemaProps(uiSchema);

	if (type === 'array') {
		// Check the items type.
		// If its an object, and it is an entity, then we
		// have a N-N relationship. Only show the entity name and the
		// select to choose the entity/ies.
		const items = schema.items as RJSFSchema;
		const type = items?.type;
		const id = items?.id || items?.$ref;
		// Check if the id is an entity
		const { entity, rel } = isEntity(id, null);
		if (type === 'object' && entity) {
			element = (
				<RelationSelect
					rel={rel}
					uiSchema={uiSchema}
					values={values}
					onChange={onChange}
				/>
			);
		} else {
			const items = schema.items as RJSFSchema;

			// This is an array of objects,
			// So we need to add a button to add more objects,
			// and the values must be an array of objects.
			const nestedValues: any[] = [];
			if (Array.isArray(values)) {
				for (const value of values) {
					nestedValues.push(value);
				}
			}

			const addNewElement = () => {
				nestedValues.push({});
				onChange(nestedValues);
			};

			const removeElement = (index: number) => {
				const newValues = nestedValues.filter((_, i) => i !== index);
				onChange(newValues);
			};

			element = (
				<>
					{nestedValues.map((value, index) => (
						<div
							className='nested-values-group mb-2 border-b pb-2'
							key={'array-' + index}>
							<>
								<JsonRenderer
									isNested
									schemaId={schemaId}
									schema={items as any as RJSFSchema}
									uiSchema={uiSchema}
									values={value}
									onChange={(arrayValue) => {
										nestedValues[index] = arrayValue;
										onChange(nestedValues);
									}}
								/>
								<div className='flex flex-row justify-end'>
									<Button
										size='sm'
										onClick={() => removeElement(index)}
										className='w-4 bg-red-700'>
										&times;
									</Button>
								</div>
							</>
						</div>
					))}

					<div className='flex flex-row justify-end'>
						<Button size='sm' onClick={addNewElement} className='w-4'>
							+
						</Button>
					</div>
				</>
			);
		}
	} else if (type === 'string') {
		if (schema.enum) {
			const options: SelectOption<string>[] = schema.enum.map((v) => ({
				value: v as string,
				label: t(`labels:select.${v && v.toString().toLowerCase()}`) as string,
			}));
			element = (
				<Select
					className={cn(elementsClassName)}
					options={options}
					value={inOptions(options, validValues)}
					onChange={(newValue) => {
						const value = newValue as SelectOption<string>;
						onChange(value.value);
					}}
				/>
			);
		} else {
			const uiWidget = uiSchema['ui:widget'] as string;

			const type = uiWidget?.startsWith('input-')
				? uiWidget?.substring('input-'.length)
				: 'text';

			const props = type === 'password' ? { autocomplete: 'off' } : {};

			element = (
				<Input
					className={cn(elementsClassName)}
					value={validValues}
					type={type}
					onChange={({ target }) => onChange(target.value)}
					{...props}
				/>
			);
		}
	} else if (type === 'number' || type === 'integer') {
		const isInteger = type === 'integer';
		element = (
			<Input
				className={cn(elementsClassName)}
				value={validValues}
				type='number'
				step={isInteger ? 1 : undefined}
				disabled={readonly}
				onChange={({ target }) => onChange(target.value)}
			/>
		);
	} else if (type === 'boolean') {
		element = (
			<Input
				className={cn(elementsClassName, 'mt-[10px] h-4 w-4')}
				checked={validValues === 'true' || validValues === true}
				type='checkbox'
				onChange={({ target }) => onChange(target.checked ? 'true' : 'false')}
			/>
		);
	}

	if (typeof uiSchema === 'object') {
		const uiWidget = uiSchema['ui:widget'] as string;
		const widgetProps = findWidget(uiWidget);

		if (widgetProps) {
			element = widgetProps({
				value: validValues,
				uiSchema,
				onChange: (value: any) => {
					onChange(value);
				},
			});
		}
	}

	return <div>{element}</div>;
};

export const useSchemaProps = (uiSchema: UiSchema) => {
	const defaults = {
		readonly: false,
		writeOnly: false,
	};

	const updated = { ...defaults };
	if (typeof uiSchema === 'object') {
		updated['readonly'] = uiSchema['ui:readonly'] ?? defaults['readonly'];
		updated['writeOnly'] = uiSchema['ui:writeonly'] ?? defaults['writeOnly'];
	}
	return updated;
};
